import React, { FC, useState } from "react";
import dashStyles from "../Dashboard/Dashboard.module.scss";
import styles from "./Admin.module.scss";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import WhitelistUser from "./WhitelistUser";
import ManagePractice from "./ManagePractice";
import MarkAttendance from "./MarkAttendance";
import ViewUser from "./ViewUser";
import Subscription from "./Subscription";

export type Permissions = Record<string, boolean> | null | undefined;

export interface SubTile {
  id: string;
  label: string;
  /** Permission flag (from the user's permissions) required to see this subtile. */
  requiredPermission: string;
  /** When true, the subtile is only shown while a practice is scheduled/in progress. */
  requiresActivePractice?: boolean;
}

interface AdminProps {
  /** Current user's permission flags. */
  permissions?: Permissions;
  /** Called with the subtile label when a subtile is opened, null when closed. */
  onSubSectionChange?: (label: string | null) => void;
  /** Called when a deeper detail level opens (e.g. View User → Details). */
  onSubSubSectionChange?: (label: string | null) => void;
  /** Incrementing this remounts ViewUser back to its list without leaving the admin subtile. */
  viewUserKey?: number;
}

// Add new Special Actions subtiles here as the section grows.
export const specialActionSubTiles: SubTile[] = [
  { id: "whitelist-user",  label: "Whitelist User",  requiredPermission: "manage_user" },
  { id: "view-user",       label: "View User",       requiredPermission: "manage_user" },
  { id: "manage-practice", label: "Manage Practice", requiredPermission: "manage_practice" },
  { id: "subscription",    label: "Subscription",    requiredPermission: "view_board_content" },
];

// Mark Attendance now lives under the dashboard "Attendance" tile, so it is no
// longer part of the Special Actions grid. It stays reachable here only as a
// shortcut from Manage Practice (clicking an active practice card), so it is
// kept out of `specialActionSubTiles` but still openable by id.
const MARK_ATTENDANCE_TILE: SubTile = {
  id: "mark-attendance",
  label: "Mark Attendance",
  requiredPermission: "mark_overall_attendance",
  requiresActivePractice: true,
};

const SUBTILE_ICONS: Record<string, React.ElementType> = {
  "whitelist-user":  PersonAddAlt1OutlinedIcon,
  "view-user":       PeopleOutlinedIcon,
  "manage-practice": EventAvailableOutlinedIcon,
  "subscription":    PaymentsOutlinedIcon,
};

/** Subtiles the given user permissions allow (respecting the active-practice gate). */
export const getVisibleSubTiles = (
  permissions: Permissions,
  hasActivePractice = false
): SubTile[] =>
  specialActionSubTiles.filter(
    (t) =>
      !!permissions &&
      permissions[t.requiredPermission] === true &&
      (!t.requiresActivePractice || hasActivePractice)
  );

/** True when the user has any Special Actions permission (drives the dashboard tile). */
export const hasVisibleSpecialActions = (permissions: Permissions): boolean =>
  specialActionSubTiles.some(
    (t) => !!permissions && permissions[t.requiredPermission] === true
  );

const Admin: FC<AdminProps> = ({ permissions, onSubSectionChange, onSubSubSectionChange, viewUserKey }) => {
  const [activeSubTile, setActiveSubTile] = useState<SubTile | null>(null);

  const openSubTile = (tile: SubTile) => {
    setActiveSubTile(tile);
    onSubSectionChange?.(tile.label);
  };

  const openSubTileById = (id: string) => {
    const tile =
      id === MARK_ATTENDANCE_TILE.id
        ? MARK_ATTENDANCE_TILE
        : specialActionSubTiles.find((t) => t.id === id);
    if (tile) openSubTile(tile);
  };

  if (activeSubTile) {
    if (activeSubTile.id === "whitelist-user") return <WhitelistUser />;
    if (activeSubTile.id === "view-user") return <ViewUser key={viewUserKey} onSubSubSectionChange={onSubSubSectionChange} />;
    if (activeSubTile.id === "subscription") return <Subscription />;
    if (activeSubTile.id === "manage-practice")
      return <ManagePractice onOpenMarkAttendance={() => openSubTileById("mark-attendance")} />;
    if (activeSubTile.id === "mark-attendance") return <MarkAttendance />;
    return null;
  }

  const visible = getVisibleSubTiles(permissions);

  return (
    <section className={styles.Admin__grid}>
      {visible.length === 0 ? (
        <div className={styles.Admin__empty}>No actions available right now.</div>
      ) : (
        visible.map((tile) => {
          const Icon = SUBTILE_ICONS[tile.id] ?? PersonAddAlt1OutlinedIcon;
          return (
            <div
              key={tile.id}
              className={dashStyles.Dashboard__tab}
              onClick={() => openSubTile(tile)}
            >
              <Icon className={dashStyles.Dashboard__tab_icon} />
              {tile.label}
            </div>
          );
        })
      )}
    </section>
  );
};

export default Admin;
